import { setAriaHidden, setAriaLabel } from '../Utils/accessibility-utils';

export interface BlogCardsProps {
    title?: string;
    linkText?: string;
    linkHref?: string;
    cards: Array<{
        tag: string;
        title: string;
        description: string;
        buttonText?: string;
        buttonHref?: string;
    }>
}

export class BlogCards {
    private element: HTMLElement;
    private props: BlogCardsProps;
    private desktopRow: HTMLElement | null = null;
    private carouselContainer: HTMLElement | null = null;
    private resizeObserver: ResizeObserver | null = null;

    constructor(element: HTMLElement, props?: BlogCardsProps) {
        this.element = element;
        this.props = props || this.getPropsFromData();
        this.init();
    }

    private getPropsFromData(): BlogCardsProps {
        const dataProps = this.element.dataset;
        let cards = [];

        try {
            if (dataProps.cards) {
                cards = JSON.parse(dataProps.cards);
            }
        } catch (e) {
            console.error('Error parsing cards data:', e);
        }

        return {
            title: dataProps.title,
            linkText: dataProps.linkText,
            linkHref: dataProps.linkHref,
            cards: cards
        };
    }

    private init(): void {
        this.desktopRow = this.element.querySelector('.blog-cards__desktop-row');
        this.carouselContainer = this.element.querySelector('.blog-cards__carousel-container');

        if (this.desktopRow) {
            this.initializeDesktopRow(this.desktopRow);
        }

        if (this.carouselContainer) {
            this.initializeCarousel(this.carouselContainer);
        }
    }

    private initializeDesktopRow(rowElement: HTMLElement): void {
        const cards = rowElement.querySelectorAll('.card') as NodeListOf<HTMLElement>;

        const updateRowVariables = (): void => {
            const rowWidth = rowElement.getBoundingClientRect().width;

            if (rowWidth > 0) {
                rowElement.style.setProperty('--row-width', `${rowWidth}px`);
                rowElement.style.setProperty('--card-count', `${cards.length}`);
            }
        };

        updateRowVariables();

        requestAnimationFrame(() => {
            updateRowVariables();
        });

        this.resizeObserver = new ResizeObserver(() => {
            updateRowVariables();
        });

        this.resizeObserver.observe(rowElement);

        let expandedCardIndex = cards.length - 1;

        cards[expandedCardIndex].classList.add('expanded');

        cards.forEach((card, index) => {
            card.addEventListener('mouseenter', () => {
                if (index !== expandedCardIndex) {
                    if (expandedCardIndex !== -1 && expandedCardIndex < cards.length) {
                        cards[expandedCardIndex].classList.remove('expanded');
                    }

                    card.classList.add('expanded');
                    expandedCardIndex = index;
                }
            });

            card.addEventListener('click', (event) => {
                if ((event.target as HTMLElement).closest('a')) {
                    return;
                }

                const href = card.getAttribute('data-href');
                if (href) {
                    window.location.href = href;
                }
            });
        });
    }

    private initializeCarousel(container: HTMLElement): void {
        const prevButton = container.querySelector('.blog-cards__carousel-prev') as HTMLElement;
        const nextButton = container.querySelector('.blog-cards__carousel-next') as HTMLElement;
        const dots = Array.from(container.querySelectorAll('.blog-cards__carousel-dot')) as HTMLElement[];
        const slidesContainer = container.querySelector('.blog-cards__carousel-slides-container') as HTMLElement;
        const cards = Array.from(container.querySelectorAll('.card')) as HTMLElement[];

        let activeCardIndex = 0;
        let isTransitioning = false;

        const positionCard = (card: HTMLElement, position: string): void => {
            card.style.transform = `translateX(${position})`;
        };

        const resetCardPositions = (): void => {
            cards.forEach((card, idx) => {
                card.style.transform = 'translateX(0)';

                if (idx === activeCardIndex) {
                    card.style.visibility = 'visible';
                    card.classList.add('active');
                    setAriaHidden(card, false);
                } else {
                    card.style.visibility = 'hidden';
                    card.classList.remove('active');
                    setAriaHidden(card, true);
                }
            });
        };

        resetCardPositions();
        dots[0].classList.add("active");

        const goToSlide = (index: number, direction: 'next' | 'prev' = 'next'): void => {
            if (isTransitioning || index === activeCardIndex) return;

            isTransitioning = true;

            const oldIndex = activeCardIndex;
            activeCardIndex = index;

            const isSmallScreen = window.innerWidth <= 407;

            const incomingCard = cards[activeCardIndex];
            const outgoingCard = cards[oldIndex];

            const outgoingFooter = outgoingCard.querySelector('.card-footer') as HTMLElement;
            if (outgoingFooter) {
                outgoingFooter.style.opacity = '0';
                outgoingFooter.style.transition = 'none';
            }

            incomingCard.classList.add('active');
            outgoingCard.classList.add('transitioning');
            outgoingCard.classList.remove('active');

            setAriaHidden(incomingCard, false);
            setAriaHidden(outgoingCard, true);

            incomingCard.style.visibility = 'visible';
            outgoingCard.style.visibility = 'visible';

            const cardWidth = isSmallScreen ?
                container.getBoundingClientRect().width :
                328;

            const gap = 16;

            if (direction === 'next') {
                positionCard(incomingCard, `${cardWidth + gap}px`);
                positionCard(outgoingCard, '0');

                incomingCard.style.zIndex = '3';
                outgoingCard.style.zIndex = '2';
            } else {
                positionCard(incomingCard, `-${cardWidth + gap}px`);
                positionCard(outgoingCard, '0');

                incomingCard.style.zIndex = '3';
                outgoingCard.style.zIndex = '2';
            }

            const incomingFooter = incomingCard.querySelector('.card-footer') as HTMLElement;
            if (incomingFooter) {
                incomingFooter.style.opacity = '0';
                incomingFooter.style.transition = 'none';
            }

            slidesContainer.offsetHeight;

            slidesContainer.classList.add(`sliding-${direction}`);

            if (direction === 'next') {
                positionCard(outgoingCard, `-${cardWidth + gap}px`);
                positionCard(incomingCard, '0');
            } else {
                positionCard(outgoingCard, `${cardWidth + gap}px`);
                positionCard(incomingCard, '0');
            }

            setTimeout(() => {
                if (incomingFooter) {
                    incomingFooter.style.transition = 'opacity 0.3s ease';
                    incomingFooter.style.opacity = '1';
                }
            }, 200);

            dots.forEach((dot, idx) => {
                if (idx === activeCardIndex) {
                    dot.classList.add('active');
                    setAriaLabel(dot, 'Current blog.');
                } else {
                    dot.classList.remove('active');
                    setAriaLabel(dot, `Go to blog ${idx + 1}.`);
                }
            });

            setTimeout(() => {
                slidesContainer.classList.remove(`sliding-${direction}`);

                cards.forEach((card, idx) => {
                    if (idx !== activeCardIndex) {
                        card.classList.remove('active', 'transitioning');
                        setAriaHidden(card, true);
                        positionCard(card, '0');
                        card.style.visibility = 'hidden';
                        card.style.zIndex = '1';

                        const footer = card.querySelector('.card-footer') as HTMLElement;
                        if (footer) {
                            footer.style.opacity = '';
                            footer.style.transition = '';
                        }
                    } else {
                        card.style.zIndex = '3';
                    }
                });

                isTransitioning = false;
            }, 500);
        };

        if (prevButton) {
            prevButton.addEventListener('click', () => {
                const newIndex = activeCardIndex === 0 ? cards.length - 1 : activeCardIndex - 1;
                goToSlide(newIndex, 'prev');
            });
        }

        if (nextButton) {
            nextButton.addEventListener('click', () => {
                const newIndex = activeCardIndex === cards.length - 1 ? 0 : activeCardIndex + 1;
                goToSlide(newIndex, 'next');
            });
        }

        dots.forEach((dot, idx) => {
            dot.addEventListener('click', () => {
                if (idx === activeCardIndex) return;

                const direction = idx > activeCardIndex ? 'next' : 'prev';
                goToSlide(idx, direction);
            });
        });

        cards.forEach((card) => {
            card.addEventListener('click', (event) => {
                if (!event.target || (event.target as HTMLElement).closest('a') || (event.target as HTMLElement).closest('.blog-cards__carousel-controls')) {
                    return;
                }

                const href = card.getAttribute('data-href');
                if (href) {
                    window.location.href = href;
                }
            });
        });
    }

    public destroy(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.blog-cards').forEach(element => {
        new BlogCards(element as HTMLElement);
    });
});